// ============================================================================
// LE REGISTRE DES INTÉGRATIONS — une intégration se DÉCLARE ici, elle ne se code pas dans l'écran.
//
// UTILITÉ : `integrations.tsx` ne connaît AUCUN service par son nom. Il rend ce registre, point.
// Ajouter Pennylane, un second transporteur ou une place de marché = ajouter une entrée ci-dessous ;
// jamais toucher au JSX. Pendant exact de `nav.ts` (la décision) face à `rail.tsx` (le rendu).
//
// ⚠️ POURQUOI CE DÉCOUPAGE ET PAS UNE CARTE PAR SERVICE. Coder « la carte Pennylane » puis « la carte
// Wallet » ferait de l'arrivée d'un connecteur une MODIFICATION D'ÉCRAN — donc une livraison, une
// relecture et un risque de régression pour ce qui n'est qu'une ligne de catalogue. C'est la Forme G
// du catalogue des défauts (« jamais de rustine, toujours un outil ») appliquée à un écran.
//
// ⚠️ DÉCLARATION ≠ ÉTAT, et c'est la distinction qui porte tout le fichier. Ce qu'une intégration EST
// (nom, rôle, capacités, secret attendu) est une donnée de conception, connue hors ligne, écrite ici.
// Ce qu'elle VAUT en ce moment (branchée ? en panne ? quelle clé ?) n'est connu que du serveur, et se
// lit par une SONDE. Un connecteur sans sonde n'est pas « déconnecté » : il n'existe pas encore, et
// l'écran doit le dire autrement (§23.0 — jamais d'état inventé, jamais d'option offerte non branchée).
// ============================================================================
import { api, type WeatherView } from './api';
import { key, t, type Translatable } from './i18n';
// ⚠️ `import type` SEULEMENT : le modèle de « Paramètres » lit CE registre pour écarter de ses onglets
//    les réglages qu'un connecteur commande — un import de valeur dans l'autre sens bouclerait au chargement.
import type { ReglageVue } from './screens/parametres-modele';
import { WALLET_SETTINGS } from './wallet-card-model';

/**
 * L'état qu'on peut AFFIRMER d'un connecteur.
 *
 * ⚠️ « À VENIR » N'EST PAS « DÉCONNECTÉ » — c'est la décision centrale de cet écran. « Déconnecté »
 * décrit un connecteur qui existe et qu'on peut brancher : il appelle une clé et un bouton. « À venir »
 * décrit un connecteur qui n'est pas construit : lui offrir un champ de clé et un test serait proposer
 * une option qui n'existe pas. Les deux se ressemblent à l'œil et n'appellent RIEN de commun.
 *
 * ⚠️ « LECTURE » EST UN ÉTAT, PAS UNE ABSENCE D'ÉTAT. Pendant l'interrogation d'une sonde, l'écran ne
 * sait rien : il doit le dire, en NEUTRE. Le ranger sous « attention » ferait clignoter en ambre toutes
 * les cartes à chaque ouverture de la page — une alerte qui ne désigne aucun problème apprend à
 * ignorer les alertes.
 */
export type StatutConnecteur = 'connecte' | 'attention' | 'erreur' | 'a_venir' | 'lecture';

/** Teinte de la pastille — un nom d'accent de la charte, jamais un littéral hexadécimal. */
export type TeinteConnecteur = 'teal' | 'blue' | 'violet' | 'amber' | 'rose';

/** Une ligne du bloc « Synchronisation » : ce que le connecteur suit, et où il en est. */
export interface IndicateurConnecteur {
  displayLabel: string;
  valeur: string;
  /** Valeur en défaut — rendue dans la teinte d'alerte plutôt qu'en encre neutre. */
  alerte?: boolean;
}

/** Un échange consigné : ce qui est parti ou arrivé, quand, et avec quel résultat. */
export interface EchangeConnecteur {
  horodatage: string;
  sens: 'sortant' | 'entrant';
  operation: string;
  /** Résultat tel que le service l'a rendu (« OK 200 », « ERR 401 ») — jamais reformulé. */
  statut: string;
  ok: boolean;
  duree: string;
  /** Contenu réellement échangé, déplié au clic. */
  contenu: string;
  /**
   * QUI l'appel concernait, quand il désigne quelqu'un.
   * ⚠️ Sans lui, un journal wallet est une liste de codes-barres : pour savoir de quel client il
   * s'agit, il fallait recopier le numéro et le chercher dans le fichier clients. Trois lignes ont
   * suffi à rendre l'écran illisible le 2026-09-07.
   */
  client: { id: string; name: string; card: string | null } | null;
}

/**
 * Ce que la carte « Connexion » montre à la place de la clé.
 *
 * ⚠️ TROIS CAS ET NON DEUX. Un secret peut être enregistré et masqué (`masque`), absent (`absent`)…
 * ou détenu par le SERVEUR et ne jamais transiter jusqu'au navigateur (`serveur`) — c'est le cas de la
 * clé météo, qui vit dans l'environnement du back-office. Rendre un champ vide pour ce dernier cas
 * ferait croire à une clé manquante, et inviterait à en saisir une qui ne serait lue nulle part.
 */
export type IdentifiantConnecteur =
  | { kind: 'masque'; masque: string; complet?: string }
  | { kind: 'serveur'; ou: string }
  | { kind: 'absent' };

/** Ce qu'une sonde rapporte : l'état vivant du connecteur, lu au serveur. */
export interface EtatConnecteur {
  statut: StatutConnecteur;
  /** Libellé de la pastille d'état — celui du service, pas une catégorie (« Clé absente au serveur »). */
  displayLabel: string;
  /** Ligne de méta sous la pastille, sur la carte de liste. */
  meta: string;
  /**
   * Bandeau d'alerte du détail. ⚠️ `remede` dit OÙ ça se répare : les motifs d'un même échec ne se
   * corrigent pas au même endroit (au serveur, dans la fiche du magasin, nulle part), et fondre les
   * quatre en « indisponible » priverait le lecteur de la seule information qui lui sert.
   */
  alerte?: { heading: string; message: string; remede: string };
  identifiant: IdentifiantConnecteur;
  indicateurs: IndicateurConnecteur[];
  /** Échanges consignés. Vide tant qu'aucun journal n'est tenu — l'écran le DIT, il ne meuble pas. */
  exchanges: EchangeConnecteur[];
}

/** La déclaration d'une intégration — tout ce qu'on sait d'elle sans interroger personne. */
export interface Connecteur {
  /** Clé de registre, stable : elle sert de sélection d'écran. */
  key: string;
  nom: Translatable;
  /** Ce que le service fait POUR LE MAGASIN, en une ligne — pas ce qu'il est. */
  description: Translatable;
  /** Initiales de la pastille, 2 à 3 caractères. ⚠️ Du texte mono, jamais une icône (dessin maquette). */
  initiales: string;
  teinte: TeinteConnecteur;
  /** Nom du secret attendu — l'en-tête du champ de clé. */
  libelleCle: Translatable;
  /** Ce que l'intégration fait, capacité par capacité. Sert de contenu au détail « à venir ». */
  capacites: Translatable[];
  /** Note de pied de la carte « Connexion » — qui peut changer le secret, et ce que la licence impose. */
  noteConnexion: Translatable;
  /**
   * LA SONDE — comment l'état RÉEL se lit. `null` = aucun connecteur n'existe : statut « à venir ».
   *
   * ⚠️ C'EST ELLE QUI AUTORISE LE BOUTON « TESTER » : un test est exactement « rejouer la sonde ».
   * Sans sonde, pas de test — l'écran n'a donc aucun moyen d'offrir un bouton qui ne testerait rien.
   */
  sonde: (() => Promise<EtatConnecteur>) | null;
  /** Le secret se saisit-il depuis l'écran ? Faux pour un réglage de déploiement. */
  identifiantsModifiables: boolean;
  /**
   * COMMENT LA CLÉ SE POSE, quand elle se pose depuis cet écran. Absent = elle ne s'y pose pas.
   * ⚠️ DÉCLARÉE, jamais codée dans le JSX : ajouter un connecteur saisissable doit rester une ligne
   * de catalogue (même règle que `sonde`). Le serveur ÉPROUVE la clé avant de la sceller, donc ce
   * qui revient est un verdict, pas un accusé de réception.
   */
  poseCle?: (cle: string) => Promise<{ ok: boolean; detail: string }>;
  /** Ce qui manque pour le mettre en place. ⚠️ À remplir SEULEMENT quand `sonde` est nulle. */
  manque?: Translatable[];
  /**
   * LES RÉGLAGES DU MAGASIN QUE CE CONNECTEUR COMMANDE — rendus dans son détail, et NULLE PART AILLEURS.
   *
   * ⚠️ 🔴 Décision utilisateur : « ce qui concerne le wallet doit vivre dans l'intégration du
   *    wallet — un client qui n'a pas le wallet aurait sinon un menu totalement inutile, tout le temps ».
   *    Et la carte du connecteur a le CONTEXTE qu'une case nue de Paramètres n'avait pas : la clé, l'état
   *    de la sonde, la liste des gabarits servie par le fournisseur. Le modèle de « Paramètres » les
   *    DÉRIVE d'ici pour les écarter de ses onglets (`REGLES_AILLEURS`) — jamais une seconde liste.
   * ⚠️ DÉCLARÉS SEULEMENT SUR UN CONNECTEUR QUI A UNE SONDE : régler un service qui n'existe pas serait
   *    offrir une option non branchée (§23.0). Invariant tenu par `integrations-registre.test.ts`.
   * ⚠️ 🔴 RENDUS PAR LA CARTE DU CONNECTEUR (`SETTINGS_CARDS` dans `screens/integrations.tsx`) : un
   *    connecteur qui déclare des réglages sans carte les rendrait invisibles PARTOUT, puisque Paramètres
   *    les écarte. Invariant tenu par `integrations-registre.test.ts`. Même route d'enregistrement que
   *    Paramètres, même gouvernance.
   */
  reglages?: ReglageVue[];
}

/**
 * L'état d'un connecteur non construit.
 *
 * ⚠️ IL N'A NI INDICATEURS NI ÉCHANGES, et c'est voulu : afficher « Dernier envoi : — » pour un
 * connecteur qui n'a jamais rien envoyé dessine le squelette d'une mécanique inexistante. Le détail
 * montre à la place ce que l'intégration FERA (`capacites`) et ce qui lui manque.
 *
 * ⚠️ UNE FONCTION, PAS UNE CONSTANTE : ses libellés se résolvent au RENDU. Une constante déclarée par
 * `key()` arrivait NUE à l'écran — qui ne peut pas savoir si un `displayLabel` est une clé ou un texte,
 * les sondes rendant des TEXTES déjà résolus ; résolue par `t()` au chargement, elle aurait figé la
 * langue de la plateforme avant que celle du compte ne soit connue.
 */
export function etatAVenir(): EtatConnecteur {
  return {
    statut: 'a_venir',
    displayLabel: t('À venir'),
    meta: t('Connecteur non construit'),
    identifiant: { kind: 'absent' },
    indicateurs: [],
    exchanges: [],
  };
}

// ---------------------------------------------------------------------------
// SONDES
// ---------------------------------------------------------------------------

/**
 * L'état du connecteur météo, lu à sa propre route.
 *
 * ⚠️ PAS DE ROUTE DE STATUT DÉDIÉE, ET C'EST SUFFISANT : `/admin/dashboard/weather` rapporte déjà sa
 * propre indisponibilité avec un motif distinct par cause — c'est-à-dire exactement ce qu'une sonde
 * doit rendre. Ajouter une seconde route qui redirait la même chose créerait deux sources de vérité
 * sur un même fait, et elles divergeraient (Forme A du catalogue des défauts).
 *
 * ⚠️ LA MENTION DE LICENCE EST RENDUE, pas rangée : le plan gratuit d'OpenWeather l'impose (ODbL).
 * Elle voyage donc jusqu'à l'écran comme un indicateur, au même titre que la température.
 */

/**
 * Traduit les échanges du serveur dans le vocabulaire de l'écran.
 *
 * ⚠️ LE STATUT EST RENDU TEL QUEL (« OK 200 », « ERR 401 »), jamais reformulé : c'est ce qui permet
 * de chercher le code dans la documentation du fournisseur. ⚠️ Et « pas de réponse » ne se confond
 * pas avec un refus — il n'a pas de code, il le DIT.
 */
function echangesDe(
  liste: {
    at: number; method: string; path: string; status: number | null; ok: boolean; durationMs: number; error: string | null;
    client?: { id: string; name: string; card: string | null } | null;
  }[],
): EchangeConnecteur[] {
  return liste.map((e) => ({
    horodatage: new Date(e.at).toLocaleString('fr-FR'),
    sens: 'sortant' as const,
    operation: `${e.method} ${e.path}`,
    statut: e.status == null ? (e.ok ? 'OK' : t('SANS RÉPONSE')) : `${e.ok ? 'OK' : 'ERR'} ${e.status}`,
    ok: e.ok,
    duree: `${e.durationMs} ms`,
    // Le motif RÉEL, ou la mention explicite qu'il n'y en a pas — jamais une phrase inventée.
    contenu: e.error ?? t('Aucune erreur signalée.'),
    // ⚠️ Les connecteurs qui n'ont pas de notion de client rendent `undefined` : ramené à `null`,
    //    parce que l'écran doit distinguer « pas de client » de « client non retrouvé ».
    client: e.client ?? null,
  }));
}

/**
 * Sonde WALLET : l'état vient du serveur (clé scellée dans la base du magasin + appel réel au
 * portefeuille). ⚠️ « Posée » ne veut pas dire « acceptée » : c'est l'appel qui tranche.
 */
async function sonderWallet(): Promise<EtatConnecteur> {
  const e = await api.integrationWallet();
  // ⚠️ MASQUÉ PAR DÉFAUT, ŒIL SUR LA FIN SEULEMENT (demande utilisateur 2026-08-31) : quatre
  //    caractères suffisent à reconnaître LAQUELLE des clés est en place, et ne permettent pas de la
  //    reconstruire. Révéler la clé entière la ferait sortir du serveur pour de bon.
  const identifiant: IdentifiantConnecteur = e.token === 'ok' && e.tokenTail
    ? { kind: 'masque', masque: '••••••••••••••••', complet: `••••••••••••${e.tokenTail}` }
    : e.token === 'ok'
      ? { kind: 'serveur', ou: t('Clé API scellée dans la base du magasin') }
      : { kind: 'absent' };

  // ⚠️ TOUS MESURÉS, aucun estimé — et `—` quand la valeur n'est pas connue plutôt que 0 : une table
  //    absente rendrait « 0 carte », un chiffre faux et plus trompeur qu'un tiret.
  const n = (v: number | null) => (v == null ? '—' : v.toLocaleString('fr-FR'));
  const depuis = (ms: number | null) => {
    if (ms == null) return t('aucun');
    const s2 = Math.max(0, Math.round((Date.now() - ms) / 1000));
    if (s2 < 60) return t('il y a {v} s', { v: s2 });
    if (s2 < 3600) return t('il y a {v} min', { v: Math.round(s2 / 60) });
    if (s2 < 86_400) return t('il y a {v} h', { v: Math.round(s2 / 3600) });
    return t('il y a {v} j', { v: Math.round(s2 / 86_400) });
  };
  const indicateurs: IndicateurConnecteur[] = [
    // « ÉMISES » et non « installées » : nous savons ce que nous avons créé, pas ce que le client a
    // posé sur son téléphone. Le mot de la maquette affirmerait une information que nul ne nous donne.
    { displayLabel: t('Cartes émises'), valeur: n(e.recap.cardsIssued) },
    { displayLabel: t('Notifications envoyées (30 j)'), valeur: n(e.recap.notifications30d) },
    {
      displayLabel: t('Soldes en attente'),
      valeur: n(e.recap.pendingBalances),
      // Une file qui stagne est le symptôme d'un transfert à l'arrêt : elle passe en alerte.
      alerte: (e.recap.pendingBalances ?? 0) > 0 && (e.recap.oldestPendingAt ?? Date.now()) < Date.now() - 3_600_000,
    },
    { displayLabel: t('Dernier échange'), valeur: depuis(e.recap.lastExchangeAt) },
    { displayLabel: 'API', valeur: e.apiUrl ?? t('adresse par défaut du fournisseur') },
  ];

  // ⚠️ L'ORDRE COMPTE : sans clé enregistrée, il n'y a RIEN à protéger — le défaut de chiffrement du
  //    serveur ne concerne pas encore ce magasin, et l'afficher lui donnerait une erreur pour un
  //    service qu'il n'utilise pas. On ne le signale qu'à ceux qui ont quelque chose en jeu.
  if (e.token === 'absent') {
    return {
      statut: 'attention', displayLabel: t('Non configuré'),
      meta: t('Aucune clé API wallet enregistrée pour ce magasin'),
      identifiant, indicateurs, exchanges: echangesDe(e.exchanges),
    };
  }
  if (!e.canSeal) {
    return {
      statut: 'erreur', displayLabel: t('Chiffrement absent'), meta: t('BO_SECRETS_KEY manquante au serveur'),
      alerte: {
        heading: t('Le serveur ne peut sceller aucun secret'),
        message: t('La clé maîtresse de chiffrement (BO_SECRETS_KEY) est absente de l’environnement du back-office : aucune clé API ne peut être enregistrée.'),
        remede: t('Renseigner BO_SECRETS_KEY dans l’environnement du serveur back-office, puis redémarrer le service.'),
      },
      identifiant, indicateurs, exchanges: echangesDe(e.exchanges),
    };
  }
  if (e.token === 'illisible') {
    // ⚠️ ILLISIBLE N'EST PAS ABSENT : la clé est là, c'est le serveur qui ne sait plus l'ouvrir. Les
    //    confondre ferait chercher une clé perdue au lieu d'une clé de chiffrement changée.
    return {
      statut: 'erreur', displayLabel: t('Clé illisible'), meta: t('chiffrement du serveur changé depuis l’enregistrement'),
      alerte: {
        heading: t('La clé enregistrée ne peut plus être déchiffrée'),
        message: t('Une clé API est bien enregistrée pour ce magasin, mais le serveur ne parvient plus à l’ouvrir — sa clé de chiffrement a changé depuis.'),
        remede: t('Ressaisir la clé API Eldowallet ci-dessous : elle sera re-scellée avec la clé courante.'),
      },
      identifiant, indicateurs, exchanges: echangesDe(e.exchanges),
    };
  }
  if (e.sonde && !e.sonde.ok) {
    return {
      statut: 'erreur', displayLabel: t('Clé refusée'), meta: e.sonde.detail,
      alerte: {
        heading: t('Le portefeuille refuse cette clé'),
        message: e.sonde.detail,
        remede: t('Vérifier la clé dans l’administration Eldowallet, puis la ressaisir ci-dessous.'),
      },
      identifiant, indicateurs, exchanges: echangesDe(e.exchanges),
    };
  }
  return {
    statut: 'connecte', displayLabel: t('Connecté'), meta: e.sonde?.detail ?? t('clé enregistrée'),
    identifiant, indicateurs, exchanges: echangesDe(e.exchanges),
  };
}

/** Sonde Pennylane : l'état vient du serveur (clé scellée + appel de lecture minimal au compte). */
async function sonderPennylane(): Promise<EtatConnecteur> {
  const e = await api.accountingPennylane();
  // ⚠️ La clé vit SCELLÉE dans la base du magasin — elle n'atteint jamais le navigateur, même masquée.
  const identifiant: IdentifiantConnecteur = { kind: 'serveur', ou: t('Clé API scellée en base magasin (posée dans Ventes & fiscal → Export comptable)') };
  const indicateurs: IndicateurConnecteur[] = [
    { displayLabel: t('Mode de dépôt'), valeur: t('Brouillons, une écriture par journée fiscale') },
  ];
  if (!e.canSeal) {
    return {
      statut: 'erreur', displayLabel: t('Chiffrement absent'), meta: t('BO_SECRETS_KEY manquante au serveur'),
      alerte: {
        heading: t('Le serveur ne peut sceller aucun secret'),
        message: t('La clé maîtresse de chiffrement (BO_SECRETS_KEY) est absente de l’environnement du back-office : aucune clé API ne peut être enregistrée.'),
        remede: t('Renseigner BO_SECRETS_KEY dans l’environnement du serveur back-office, puis redémarrer le service.'),
      },
      identifiant, indicateurs, exchanges: [],
    };
  }
  // ⚠️ PAS DE CLÉ N'EST PAS UNE PANNE (retour utilisateur 2026-08-31). Un magasin qui n'utilise pas
  //    Pennylane tombait sinon dans « Clé refusée » — sonde à `null`, donc « sans verdict » — et
  //    portait un grand bandeau ROUGE en permanence pour un service qu'il n'a jamais souscrit. La
  //    pastille ambre « Non configuré » suffit : elle se voit sans alarmer, et le bandeau reste
  //    réservé à ce qui est VRAIMENT cassé.
  //    ⚠️ Cette branche existait ; je l'avais supprimée par erreur en éditant la mauvaise sonde. Le
  //    bandeau rouge signalé par l'utilisateur venait de là.
  if (!e.configured) {
    return {
      statut: 'attention', displayLabel: t('Non configuré'),
      meta: t('Aucune clé API Pennylane enregistrée pour ce magasin'),
      identifiant: { kind: 'absent' }, indicateurs, exchanges: [],
    };
  }
  return e.probe?.ok
    ? { statut: 'connecte', displayLabel: t('Connecté'), meta: e.probe.detail, identifiant, indicateurs, exchanges: [] }
    : {
      statut: 'erreur', displayLabel: t('Clé refusée'), meta: e.probe?.detail ?? t('Sonde sans verdict'),
      alerte: {
        heading: t('Le compte ne répond plus à la clé'),
        message: e.probe?.detail ?? t('La sonde n’a pas rendu de verdict.'),
        remede: t('Vérifier la clé côté Pennylane (révoquée ? droits ?) puis en poser une nouvelle dans Ventes & fiscal → Export comptable.'),
      },
      identifiant, indicateurs, exchanges: [],
    };
}

async function sonderMeteo(): Promise<EtatConnecteur> {
  const v: WeatherView = await api.weather();
  // ⚠️ La clé est une variable d'environnement du back-office : elle n'atteint JAMAIS le navigateur.
  const identifiant: IdentifiantConnecteur = { kind: 'serveur', ou: t('Variable OPENWEATHER_API_KEY du serveur back-office') };

  if (v.ok) {
    return {
      statut: 'connecte',
      displayLabel: t('Connecté'),
      meta: `${v.city} — ${v.tempC}°, ${v.description}`,
      identifiant,
      indicateurs: [
        { displayLabel: t('Ville résolue'), valeur: v.city },
        { displayLabel: t('Relevé'), valeur: t('{tempC}° (ressenti {feelsC}°) — {description}', { tempC: v.tempC, feelsC: v.feelsC, description: v.description }) },
        { displayLabel: t('Lecture'), valeur: t('À chaque ouverture du tableau de bord') },
        { displayLabel: t('Attribution'), valeur: v.attribution },
      ],
      exchanges: [],
    };
  }

  const motifs: Record<typeof v.reason, { statut: StatutConnecteur; displayLabel: string; heading: string; message: string; remede: string }> = {
    not_configured: {
      // ⚠️ « ATTENTION » ET NON « À VENIR » : le connecteur EXISTE, il lui manque une clé. Le ranger
      //    parmi les chantiers ferait chercher du code là où il n'y a qu'un réglage à poser.
      statut: 'attention', displayLabel: t('Non configuré'),
      heading: t('Aucune clé de service'),
      message: t('Le back-office n’a pas de clé OpenWeather : la carte météo du tableau de bord dit son absence plutôt que d’afficher un temps plausible.'),
      remede: t('Renseigner OPENWEATHER_API_KEY dans l’environnement du serveur back-office, puis redémarrer le service.'),
    },
    no_location: {
      statut: 'attention', displayLabel: t('Magasin non situé'),
      heading: t('Le magasin n’a pas d’adresse'),
      message: t('La météo est cadrée sur le CODE POSTAL du magasin, celui-là même que la caisse fige comme identité de l’émetteur sur chaque pièce fiscale. Il est vide.'),
      remede: t('Renseigner le code postal et la ville dans Réglages → Magasin & caisses.'),
    },
    unsupported_country: {
      statut: 'attention', displayLabel: t('Pays hors couverture'),
      heading: t('Pays non couvert'),
      message: t('Le pays déclaré du magasin n’est pas pris en charge par le service.'),
      remede: t('Vérifier le pays dans Réglages → Magasin & caisses ; s’il est juste, le service ne couvre pas cette zone.'),
    },
    provider_failed: {
      statut: 'erreur', displayLabel: t('Erreur'),
      heading: t('Le service n’a pas répondu'),
      message: v.detail ? t('Réponse du service : {detail}', { detail: v.detail }) : t('Le service n’a pas répondu à la dernière lecture.'),
      remede: t('Rien à corriger de notre côté — la lecture suivante réessaiera. Si l’échec persiste, vérifier la validité de la clé.'),
    },
  };
  const m = motifs[v.reason];
  return {
    statut: m.statut,
    displayLabel: m.displayLabel,
    meta: m.heading,
    alerte: { heading: m.heading, message: m.message, remede: m.remede },
    identifiant,
    indicateurs: [],
    exchanges: [],
  };
}

// ---------------------------------------------------------------------------
// LE REGISTRE
// ---------------------------------------------------------------------------

/**
 * LES INTÉGRATIONS DU MAGASIN, dans l'ordre où elles se lisent.
 *
 * ⚠️ LA MÉTÉO OUVRE LA LISTE PARCE QU'ELLE EST LA SEULE QUI EXISTE (2026-08-22). Elle ne figure pas
 * dans la maquette — dessinée avant qu'elle ne soit construite — mais elle est exactement ce que cet
 * écran décrit : un service externe, avec une clé, un état et un motif de panne. La ranger ailleurs
 * aurait laissé la page entière en « à venir », c'est-à-dire sans aucun chemin réel à vérifier.
 *
 * ⚠️ NE SONT PAS ICI, ET LA NOTE DE PIED DE L'ÉCRAN LE DIT : les terminaux Mobile Partner (matériel du
 * magasin, déclaré par la Console et listé dans Magasin & caisses) et l'EDI fournisseurs (flux d'achat →
 * Commandes & BL). Les ranger ici ferait chercher au mauvais endroit ce qui vit déjà ailleurs.
 */
export const CONNECTEURS: Connecteur[] = [
  {
    key: 'meteo',
    nom: key('Météo du magasin'),
    description: key('Le temps sur le point de vente, pour lire les ventes du jour'),
    initiales: 'MTO',
    teinte: 'blue',
    libelleCle: key('Clé de service OpenWeather'),
    capacites: [
      key('Relevé du jour sur le tableau de bord, cadré sur le code postal du magasin'),
      key('Motif explicite quand la lecture échoue, jamais un temps de repli'),
    ],
    noteConnexion:
      key('Cette clé est un réglage de DÉPLOIEMENT, pas un réglage d’écran : elle vit dans l’environnement du serveur back-office et n’atteint jamais le navigateur. Données © OpenWeather, licence ODbL — la mention est une condition d’usage, pas une décoration.'),
    sonde: sonderMeteo,
    identifiantsModifiables: false,
  },
  {
    key: 'pennylane',
    nom: key('Pennylane'),
    description: key('Écritures comptables poussées par API'),
    initiales: 'PL',
    teinte: 'teal',
    libelleCle: key('Clé API Pennylane'),
    capacites: [
      key('Envoi des écritures de vente en BROUILLON, à valider par le comptable'),
      key('Un envoi par journée comptable, rejouable sans doubler (clé d’idempotence)'),
      key('Compte rendu par envoi : accepté, refusé, motif du refus'),
    ],
    noteConnexion:
      key('La clé se pose dans Ventes & fiscal → Export comptable (bloc API Pennylane) — sondée avant d’être scellée, jamais relue vers un écran.'),
    sonde: sonderPennylane,
    identifiantsModifiables: false,
  },
  {
    key: 'messagerie',
    nom: key('Plateforme SMS & Email'),
    description: key('Campagnes marketing — envois et crédits'),
    initiales: 'SMS',
    teinte: 'violet',
    libelleCle: key('Clé de compte plateforme'),
    capacites: [
      key('Envoi de campagnes SMS et email au fichier clients'),
      key('Suivi des crédits restants, par canal'),
      key('Retours de livraison (DLR) et désinscriptions remontés en temps réel'),
    ],
    noteConnexion:
      key('Modifiable par le gérant ou le staff Orgazone (console). Chaque modification sera tracée au journal.'),
    sonde: null,
    identifiantsModifiables: false,
    manque: [
      key('Le module MARKETING (Clients → Marketing) : sans segments ni campagnes, il n’y a rien à envoyer.'),
      key('Le consentement client (opt-in / opt-out) au fichier clients, sans lequel aucun envoi n’est licite.'),
    ],
  },
  {
    key: 'wallet',
    nom: key('Wallet'),
    description: key('Cartes Apple/Google Wallet et notifications push'),
    initiales: 'WLT',
    teinte: 'amber',
    libelleCle: key('Identifiant émetteur'),
    capacites: [
      key('Carte de fidélité dématérialisée, installée depuis un lien ou un QR'),
      key('Mise à jour du solde de fidélité sur la carte, sans réinstallation'),
      key('Notification push à l’occasion d’une campagne'),
      key('Les titres du client — chèque cadeau, bon de fidélité, avoir, acompte — dans son portefeuille, au solde qui lui reste'),
    ],
    noteConnexion:
      key('La clé API de VOTRE compte Eldowallet, pour CE magasin. Elle est éprouvée chez le fournisseur avant d’être scellée, et ne redescend jamais ensuite — elle ne peut être que remplacée. L’éditeur peut aussi la poser depuis la console, notamment pour plusieurs magasins d’un coup.'),
    sonde: sonderWallet,
    identifiantsModifiables: true,
    poseCle: (cle) => api.integrationWalletKey(cle),
    // ⚠️ DÉRIVÉS du modèle de la carte wallet (`wallet-card-model.ts`) : la carte et le registre ne
    //    peuvent pas diverger sur ce que le connecteur règle.
    reglages: WALLET_SETTINGS,
  },
  {
    key: 'ecommerce',
    nom: key('E-commerce'),
    description: key('Stocks et prix poussés vers la boutique en ligne'),
    initiales: 'WEB',
    teinte: 'rose',
    libelleCle: key('Jeton d’accès boutique'),
    capacites: [
      key('Envoi des niveaux de stock et des prix vers la boutique'),
      key('Descente des commandes en ligne vers le magasin'),
      key('Alerte au centre de notifications dès que la synchronisation échoue'),
    ],
    noteConnexion:
      key('Modifiable par le gérant ou le staff Orgazone (console). Chaque modification sera tracée au journal.'),
    sonde: null,
    identifiantsModifiables: false,
    manque: [
      key('Le choix de la PLATEFORME cible : le connecteur d’une boutique n’est pas celui d’une autre, et rien n’est arbitré.'),
      key('Le stock TEMPS RÉEL consolidé côté serveur, sans lequel on publierait en ligne une disponibilité fausse.'),
    ],
  },
];

/** Le connecteur d'une clé — `null` sur une clé inconnue, jamais un repli sur le premier de la liste. */
export function connecteurDe(key: string | null): Connecteur | null {
  if (!key) return null;
  return CONNECTEURS.find((c) => c.key === key) ?? null;
}

/** Fond et encre de la pastille d'initiales, depuis la teinte déclarée. */
export function teintesDe(teinte: TeinteConnecteur): { bg: string; fg: string } {
  if (teinte === 'teal') return { bg: 'var(--teal-100)', fg: 'var(--teal-800)' };
  // ⚠️ FORME CQ : une RÉFÉRENCE À UNE VARIABLE CSS n'est pas un texte. `i18n-gabarits` y a vu une
  //    phrase à trou parce qu'elle EN A LA FORME — traduite, la couleur du connecteur casse en
  //    silence. C'est la garde INVERSE (« cette clé est-elle un texte ? ») qui l'a attrapée.
  return { bg: `var(--${teinte}-bg)`, fg: `var(--${teinte}-fg)` };
}

/**
 * Les couleurs d'un statut — fond, encre, point et bordure de carte.
 *
 * ⚠️ « À VENIR » EST NEUTRE, JAMAIS ROUGE : ce n'est pas une panne, c'est un chantier. Le peindre en
 * alerte enverrait chercher une réparation là où il n'y a rien de cassé.
 */
export function couleursStatut(s: StatutConnecteur): { bg: string; fg: string; dot: string; bd: string } {
  if (s === 'connecte') return { bg: 'var(--teal-100)', fg: 'var(--teal-800)', dot: 'var(--teal-500)', bd: 'var(--ink-200)' };
  if (s === 'lecture') return { bg: 'var(--ink-100)', fg: 'var(--ink-600)', dot: 'var(--ink-300)', bd: 'var(--ink-200)' };
  if (s === 'attention') return { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', dot: 'var(--amber)', bd: 'var(--amber)' };
  if (s === 'erreur') return { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', dot: 'var(--rose-fg)', bd: 'var(--rose)' };
  return { bg: 'var(--ink-100)', fg: 'var(--ink-600)', dot: 'var(--ink-400)', bd: 'var(--ink-200)' };
}
